import {
  DndContext,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DragSelectionProvider, useDragSelectionContextOptional } from "@/contexts/DragSelectionContext";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import type { Assignment, ProjectSummary } from "@/hooks/useProjectBoardData";
import { useRoles } from "@/hooks/useRoles";
import { db } from "@/lib/supabase";
import { diffRecords, fullRowChanges, recordAudit } from "@/lib/audit";
import { assignmentAuditLabel, projectAuditLabel } from "@/utils/auditLabels";
import { withWeeklyHoursSummary } from "@/utils/auditWeeklyHours";
import type { Database } from "@/types/database.types";
import { MILLISECONDS_PER_DAY, addWeeks, parseISODate } from "@/utils/dateUtils";
import { getProjectWeekIndex } from "@/utils/projectUtils";
import { computeWeekColumns, type WeekColumn } from "@/utils/weekUtils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AssignmentRow } from "./AssignmentRow";
import {
  ProjectHeaderBar,
  ProjectSummaryColumn,
  type ProjectSummaryColumnProps,
} from "./ProjectSummaryColumn";
import type { ProjectAvailabilityFocus } from "./ProjectAvailabilityDock";
import { ProjectWeekHeaderRow } from "./ProjectWeekHeaderRow";
import { useStickyProjectHeader } from "./StickyProjectHeaderContext";

/** A role id (uuid). */
type EmployeeRole = string;

interface ProjectAssignmentsProps {
  project: ProjectSummary;
  timelineStartDate: string;
  onRefresh: () => void;
  overAllocatedEmployeeIds: Set<string>;
  employeeProjectLabels?: Map<string, string[]>;
  updateProjectOptimistically: (
    projectId: string,
    updates: Partial<ProjectSummary>
  ) => void;
  refreshSilently: () => Promise<void>;
  projectSummaryColumnProps: ProjectSummaryColumnProps;
  onOpenAvailabilityForAssignment?: (focus: ProjectAvailabilityFocus) => void;
  focusedAvailabilityAssignmentId?: string | null;
}

/**
 * Component displaying all assignments for a project.
 * Wraps content with DragSelectionProvider to enable bulk hour editing.
 */
export function ProjectAssignments({
  project,
  timelineStartDate,
  onRefresh,
  overAllocatedEmployeeIds,
  employeeProjectLabels,
  updateProjectOptimistically,
  refreshSilently,
  projectSummaryColumnProps,
  onOpenAvailabilityForAssignment,
  focusedAvailabilityAssignmentId,
}: ProjectAssignmentsProps) {
  const weeks = useMemo(
    () => {
      const weeksStartDate = timelineStartDate ? new Date(timelineStartDate) : new Date();
      const projectStartDate = parseISODate(project.start_date);
      const projectEnd = addWeeks(projectStartDate, project.num_weeks);
      const numWeeks = Math.ceil(
        (projectEnd.getTime() - weeksStartDate.getTime()) / (7 * MILLISECONDS_PER_DAY)
      );
      return computeWeekColumns(weeksStartDate, numWeeks);
    },
    [project, timelineStartDate]
  );

  return (
    <DragSelectionProvider>
      <ProjectAssignmentsContent
        project={project}
        weeks={weeks}
        onRefresh={onRefresh}
        overAllocatedEmployeeIds={overAllocatedEmployeeIds}
        employeeProjectLabels={employeeProjectLabels}
        updateProjectOptimistically={updateProjectOptimistically}
        refreshSilently={refreshSilently}
        projectSummaryColumnProps={projectSummaryColumnProps}
        onOpenAvailabilityForAssignment={onOpenAvailabilityForAssignment}
        focusedAvailabilityAssignmentId={focusedAvailabilityAssignmentId}
      />
    </DragSelectionProvider>
  );
}

interface ProjectAssignmentsContentProps {
  project: ProjectSummary;
  weeks: WeekColumn[];
  onRefresh: () => void;
  overAllocatedEmployeeIds: Set<string>;
  employeeProjectLabels?: Map<string, string[]>;
  updateProjectOptimistically: (
    projectId: string,
    updates: Partial<ProjectSummary>
  ) => void;
  refreshSilently: () => Promise<void>;
  projectSummaryColumnProps: ProjectSummaryColumnProps;
  onOpenAvailabilityForAssignment?: (focus: ProjectAvailabilityFocus) => void;
  focusedAvailabilityAssignmentId?: string | null;
}

/**
 * Inner content component that has access to the drag selection context.
 * This separation allows the bulk update handler to access the context.
 */
function ProjectAssignmentsContent({
  project,
  weeks,
  onRefresh,
  overAllocatedEmployeeIds,
  employeeProjectLabels,
  updateProjectOptimistically,
  refreshSilently,
  projectSummaryColumnProps,
  onOpenAvailabilityForAssignment,
  focusedAvailabilityAssignmentId,
}: ProjectAssignmentsContentProps) {
  const [addingRole, setAddingRole] = useState(false);
  const dragSelection = useDragSelectionContextOptional();
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const { roleSortOrder, formatRole } = useRoles();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isSyncingScrollRef = useRef(false);
  const cloneCleanupRef = useRef<(() => void) | null>(null);
  const [floatBounds, setFloatBounds] = useState<{ left: number; width: number } | null>(null);

  // This project's header floats only while it's the active sticky project.
  // Registration of every project row (expanded or collapsed) as a sentinel
  // happens in ProjectRow, so the floating header detaches as soon as the next
  // project — open or not — crosses the line.
  const stickyContext = useStickyProjectHeader();
  const activeProjectId = stickyContext?.activeProjectId ?? null;
  const isFloating = activeProjectId === project.id;

  // Measure the inline scroll container's left/width so the clone spans the
  // visible width of the inline project assignments and aligns underneath the
  // floating ProjectsControls bar above it.
  useEffect(() => {
    if (!isFloating) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const measure = () => {
      const rect = scrollEl.getBoundingClientRect();
      setFloatBounds({ left: rect.left, width: rect.width });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scrollEl);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isFloating]);

  // Sync horizontal scroll between the inline scroll container and the floating
  // clone via a callback ref. Callback refs fire exactly when the DOM node is
  // attached or detached, avoiding the multi-commit timing fragility of a
  // useEffect that depends on both `isFloating` and `floatBounds`.
  const attachClone = useCallback((node: HTMLDivElement | null) => {
    cloneCleanupRef.current?.();
    cloneCleanupRef.current = null;
    if (!node) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    node.scrollLeft = scrollEl.scrollLeft;
    const onTableScroll = () => {
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      node.scrollLeft = scrollEl.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };
    const onCloneScroll = () => {
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      scrollEl.scrollLeft = node.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };
    scrollEl.addEventListener("scroll", onTableScroll);
    node.addEventListener("scroll", onCloneScroll);
    cloneCleanupRef.current = () => {
      scrollEl.removeEventListener("scroll", onTableScroll);
      node.removeEventListener("scroll", onCloneScroll);
    };
  }, []);

  const handleAddRole = async (role: EmployeeRole) => {
    if (addingRole) return;
    setAddingRole(true);
    const numWeeks = project.num_weeks ?? 1;
    const weeklyHoursAllocated = Array.from({ length: numWeeks }, () => 0);
    const assignmentId = crypto.randomUUID();
    const maxExisting = project.project_assignments.reduce(
      (max, a) => Math.max(max, a.display_order ?? 0),
      -1
    );
    const nextDisplayOrder = maxExisting + 1;
    const optimisticAssignment: Assignment = {
      id: assignmentId,
      project_id: project.id,
      required_role_id: role,
      weekly_hours_allocated: weeklyHoursAllocated,
      is_confirmed: false,
      notes: null,
      display_order: nextDisplayOrder,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      employees: [],
    };
    updateProjectOptimistically(project.id, {
      project_assignments: [...project.project_assignments, optimisticAssignment],
    });
    try {
      const insertData: Database["public"]["Tables"]["project_assignments"]["Insert"] = {
        id: assignmentId,
        project_id: project.id,
        required_role_id: role,
        weekly_hours_allocated: weeklyHoursAllocated,
        is_confirmed: false,
        display_order: nextDisplayOrder,
      };
      markSelfEdit("project_assignments", assignmentId);
      const { data, error } = await db.projectAssignments.create(insertData);
      if (error) {
        await refreshSilently();
      } else {
        recordAudit({
          table: "project_assignments",
          recordId: data.id,
          operation: "INSERT",
          changes: withWeeklyHoursSummary(
            fullRowChanges(data, "INSERT"),
            project.start_date
          ),
          recordLabel: assignmentAuditLabel(projectAuditLabel(project), optimisticAssignment, formatRole),
          officeId: project.office_id ?? null,
        });
      }
    } finally {
      setAddingRole(false);
    }
  };

  /**
   * Handle bulk hour updates from drag selection.
   * This is the core business logic for applying bulk changes to assignment hours.
   * 
   * When integrating with TimelineRow, cells should use:
   * - dragSelection.handlePointerDown(e, assignmentId, weekKey) on each cell
   * - dragSelection.registerRowGrid(assignmentId, element, cellKeys) on each row
   */
  const handleBulkUpdate = useCallback(async () => {
    if (!dragSelection) return;
    
    const updates = dragSelection.commitBulkInput();
    if (!updates || updates.length === 0) return;

    // Group updates by assignment (rowId)
    const updatesByAssignment = new Map<string, Array<{ cellKey: number; value: number }>>();
    for (const update of updates) {
      const assignmentUpdates = updatesByAssignment.get(update.rowId) ?? [];
      assignmentUpdates.push({ cellKey: update.cellKey, value: update.value });
      updatesByAssignment.set(update.rowId, assignmentUpdates);
    }

    // Apply updates to each assignment
    const optimisticAssignments = [...project.project_assignments];
    const assignmentIdsToUpdate: Array<{
      id: string;
      hoursArray: number[];
      oldHours: number[];
      assignment: Assignment;
    }> = [];

    for (const [assignmentId, cellUpdates] of updatesByAssignment) {
      const assignment = project.project_assignments.find(a => a.id === assignmentId);
      if (!assignment) continue;

      const currentHours = assignment.weekly_hours_allocated ?? [];
      const hoursArray = Array.from({ length: project.num_weeks }, (_, index) => {
        const cellUpdate = cellUpdates.find(u => {
          const weekIndex = getProjectWeekIndex(u.cellKey, weeks, project.start_date, project.num_weeks);
          return weekIndex === index;
        });
        return cellUpdate ? cellUpdate.value : (currentHours[index] ?? 0);
      });

      // Update optimistic state
      const optimisticIdx = optimisticAssignments.findIndex(a => a.id === assignmentId);
      if (optimisticIdx !== -1) {
        const existing = optimisticAssignments[optimisticIdx]!;
        optimisticAssignments[optimisticIdx] = {
          ...existing,
          weekly_hours_allocated: hoursArray,
        } as Assignment;
      }

      assignmentIdsToUpdate.push({ id: assignmentId, hoursArray, oldHours: currentHours, assignment });
    }

    // Apply optimistic update
    updateProjectOptimistically(project.id, {
      project_assignments: optimisticAssignments,
    });

    // Execute DB updates
    let hasErrors = false;
    for (const { id, hoursArray, oldHours, assignment } of assignmentIdsToUpdate) {
      markSelfEdit("project_assignments", id);
      const { error } = await db.projectAssignments.update(id, {
        weekly_hours_allocated: hoursArray,
      });
      if (error) {
        hasErrors = true;
      } else {
        recordAudit({
          table: "project_assignments",
          recordId: id,
          operation: "UPDATE",
          changes: withWeeklyHoursSummary(
            diffRecords(
              { weekly_hours_allocated: oldHours },
              { weekly_hours_allocated: hoursArray }
            ),
            project.start_date
          ),
          recordLabel: assignmentAuditLabel(projectAuditLabel(project), assignment, formatRole),
          officeId: project.office_id ?? null,
        });
      }
    }
    
    if (hasErrors) {
      console.error("Failed to update some assignments");
      await refreshSilently();
    }
  }, [dragSelection, project, weeks, updateProjectOptimistically, refreshSilently]);

  // Order rows purely by persisted display_order (the data hooks already
  // pre-sort, but this is defense in depth — especially while optimistic state
  // is being applied during drag/add operations). Ties fall back to created_at.
  const sortedEmployeeAssignments = useMemo(() => {
    return [...project.project_assignments].sort((a, b) => {
      const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      const aCreated = a.created_at ?? "";
      const bCreated = b.created_at ?? "";
      return aCreated.localeCompare(bCreated);
    });
  }, [project.project_assignments]);

  const assignmentIds = useMemo(
    () => sortedEmployeeAssignments.map((a) => a.id),
    [sortedEmployeeAssignments]
  );

  const sortableSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const persistDisplayOrder = useCallback(
    async (orderedAssignments: Assignment[]) => {
      const updates = orderedAssignments.map((a, idx) =>
        db.projectAssignments.update(a.id, { display_order: idx })
      );
      const results = await Promise.all(updates);
      const hasError = results.some((r) => r.error);
      if (hasError) {
        await refreshSilently();
      } else {
        await refreshSilently();
      }
    },
    [refreshSilently]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sortedEmployeeAssignments.findIndex((a) => a.id === active.id);
      const newIndex = sortedEmployeeAssignments.findIndex((a) => a.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(sortedEmployeeAssignments, oldIndex, newIndex).map(
        (a, idx) => ({ ...a, display_order: idx })
      );
      // Reordering renumbers display_order on every shifted row, but only the
      // dragged row should flash. Suppress the realtime flash for the rows that
      // were merely renumbered so the flash lands on the dragged item alone.
      reordered.forEach((a) => {
        if (a.id !== active.id) markSelfEdit("project_assignments", a.id);
      });
      updateProjectOptimistically(project.id, {
        project_assignments: reordered,
      });
      await persistDisplayOrder(reordered);
    },
    [sortedEmployeeAssignments, project.id, updateProjectOptimistically, persistDisplayOrder, markSelfEdit]
  );

  const [sortingRoles, setSortingRoles] = useState(false);

  const handleSortRoles = useCallback(async () => {
    if (sortingRoles) return;
    setSortingRoles(true);
    try {
      const reordered = [...project.project_assignments]
        .sort((a, b) => {
          const roleDiff = roleSortOrder(a.required_role_id) - roleSortOrder(b.required_role_id);
          if (roleDiff !== 0) return roleDiff;
          const aName = a.employees[0]?.name ?? "";
          const bName = b.employees[0]?.name ?? "";
          const nameDiff = aName.localeCompare(bName);
          if (nameDiff !== 0) return nameDiff;
          return a.id.localeCompare(b.id);
        })
        .map((a, idx) => ({ ...a, display_order: idx }));
      updateProjectOptimistically(project.id, {
        project_assignments: reordered,
      });
      await persistDisplayOrder(reordered);
    } finally {
      setSortingRoles(false);
    }
  }, [sortingRoles, project.project_assignments, project.id, updateProjectOptimistically, persistDisplayOrder, roleSortOrder]);

  const isEmpty = sortedEmployeeAssignments.length === 0;

  return (
    <div
      className="project-assignments"
      data-testid="project-assignments"
      data-project-id={project.id}
      data-project-name={project.name}
    >
      <div
        className="project-assignments__table-scroll"
        ref={scrollRef}
        onPointerMove={dragSelection?.handlePointerMove}
        onPointerUp={dragSelection?.handlePointerUp}
      >
        <div className="project-assignments__header-row">
          <ProjectHeaderBar
            headerRowProps={projectSummaryColumnProps.headerRowProps}
            onAddRole={(role) => handleAddRole(role)}
            addRoleDisabled={addingRole}
            onSortRoles={handleSortRoles}
            sortRolesDisabled={sortingRoles || addingRole}
          />
          <ProjectWeekHeaderRow weeks={weeks} />
        </div>
        <div className="project-assignments__body">
          <div className="project-assignments__meta-column">
            <ProjectSummaryColumn
             {...projectSummaryColumnProps} />
          </div>
          <div className="project-assignments__table">
            {isEmpty ? (
              <div className="project-assignments__empty">No roles assigned yet.</div>
            ) : (
              <DndContext
                sensors={sortableSensors}
                onDragEnd={handleDragEnd}
                autoScroll={false}
                measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
              >
                <SortableContext items={assignmentIds} strategy={verticalListSortingStrategy}>
                  {sortedEmployeeAssignments.map((assignment) => (
                    <AssignmentRow
                      key={assignment.id}
                      assignment={assignment}
                      weeks={weeks}
                      project={project}
                      overAllocatedEmployeeIds={overAllocatedEmployeeIds}
                      employeeProjectLabels={employeeProjectLabels}
                      onRefresh={onRefresh}
                      updateProjectOptimistically={updateProjectOptimistically}
                      refreshSilently={refreshSilently}
                      onOpenAvailabilityForAssignment={onOpenAvailabilityForAssignment}
                      isAvailabilityFocused={assignment.id === focusedAvailabilityAssignmentId}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
      {isFloating && floatBounds && (
        <div
          ref={attachClone}
          className="project-week-header-floating"
          style={{ left: `${floatBounds.left}px`, width: `${floatBounds.width}px` }}
        >
          <ProjectHeaderBar
            headerRowProps={projectSummaryColumnProps.headerRowProps}
            onAddRole={(role) => handleAddRole(role)}
            addRoleDisabled={addingRole}
            onSortRoles={handleSortRoles}
            sortRolesDisabled={sortingRoles || addingRole}
          />
          <ProjectWeekHeaderRow weeks={weeks} />
        </div>
      )}
      {/* Batch update bar - shown when multiple cells are selected */}
      {dragSelection?.showBulkInput && dragSelection.selectedItems.size > 1 && (
        <div className="batch-update-bar-wrapper">
          <div className="batch-update-bar">
            <span className="batch-update-bar__label">
              {dragSelection.selectedItems.size} cells
            </span>
            <input
              type="number"
              className="batch-update-bar__input"
              value={dragSelection.editingValue}
              onChange={(e) => dragSelection.setEditingValue(e.target.value)}
              onBlur={handleBulkUpdate}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleBulkUpdate();
                } else if (e.key === "Escape") {
                  dragSelection.cancelSelection();
                }
              }}
              autoFocus
              min="0"
              step="1"
              placeholder="0"
            />
          </div>
        </div>
      )}
    </div>
  );
}
